import { router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { accountSwitchPath, newAccountPath } from "@travily/routes";
import { DialogTitle } from "@ui/components/Dialog";
import { Icon } from "@ui/components/Icon";
import { Page } from "@ui/components/Page";
import {
  SearchSectionStack,
  type SearchSectionStackItem,
  WhenEndPanel,
  type WhenSelection,
  WhenStartPanel,
  WherePopover,
  WhereSearchPanel,
  type WhereSelection,
  WhoPanel,
  type WhoSelection,
  whoSummary,
} from "@ui/components/SearchBar";
import { StandardButton } from "@ui/components/StandardButton";
import { SubNav, SubNavItem } from "@ui/components/SubNav";
import { Button } from "@ui/primitives/Button";
import { type OverlayTriggerState, OverlayTriggerStateContext } from "@ui/primitives/Modal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AddPersonSheet } from "@/components/AddPersonSheet/AddPersonSheet";
import { MarketplaceSearchBar } from "@/components/MarketplaceSearch/MarketplaceSearchBar";
import { ShowView as UnderConstructionView } from "@/components/UnderConstructionView/ShowView";
import { useSearchBarWhere } from "@/hooks/use-search-bar-where";
import {
  DEFAULT_EXPLORE_SECTION,
  EXPLORE_SECTIONS,
  type ExploreSection,
  sectionForPath,
} from "@/layouts/components/explore-sections";
import {
  MARKETPLACE_QUERY_PARAM,
  serializeWhenParams,
  serializeWhereParams,
  serializeWhoParams,
} from "@/lib/search-params";
import type { SharedProps } from "@/types";

const VISIT_OPTIONS = { preserveState: false, preserveScroll: false } as const;

const EMPTY_WHEN: WhenSelection = { start: null, end: null };
const EMPTY_WHO: WhoSelection = { memberIds: [] };

// The search content sits in a white rounded card with the design's layered shadow (Figma
// "Drop / Where"). `overflow-hidden` clips the reused WherePopover's content to the rounded corners.
const SEARCH_CARD = "overflow-hidden rounded-3xl bg-white shadow-md";

// `WherePopover` calls `overlayState.close()` after a selection to dismiss its popover. The Silk
// page provides no RAC overlay context, so shadow it with an explicit no-op so that close() can
// never dismiss an ancestor RAC overlay — the popover content controls its own dismissal here.
const NOOP_OVERLAY_STATE = {
  isOpen: true,
  setOpen: () => {},
  open: () => {},
  close: () => {},
  toggle: () => {},
} as unknown as OverlayTriggerState;

const CLOSE_BUTTON_CLASS =
  "flex size-9 cursor-pointer items-center justify-center rounded-full text-neutral-600 outline-none hover:bg-neutral-50 hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-neutral-400";

export interface ExploreSearchOverlayProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Current path (query stripped) — picks the default active tab. */
  currentPath: string;
  /** Whether the signed-in viewer may use search. Non-active signed-in viewers (e.g. waitlisted) see
   *  Under Construction instead. Signed-out viewers are routed to login before this opens. The
   *  destination section pages enforce `:active` server-side regardless. */
  isActive: boolean;
}

/**
 * Full-screen mobile search overlay opened from the Explore search pill. For active viewers: sub-nav
 * tabs switch what's searched (Airbnb-style), the body is contextual — a stacked Where/When/Who
 * accordion for destinations, location autocomplete for programs, free-text for marketplace — and
 * submitting `router.get`s the section index with the search encoded in the URL (which the section
 * page reads back to seed its state). For signed-in non-active viewers it shows the Under Construction
 * view (signed-out viewers are routed to login before it opens); the real access gate lives on the
 * destination pages (`page_access :active`).
 */
export function ExploreSearchOverlay({ isOpen, onOpenChange, currentPath, isActive }: ExploreSearchOverlayProps) {
  const [activeSection, setActiveSection] = useState<ExploreSection>(
    () => sectionForPath(currentPath) ?? DEFAULT_EXPLORE_SECTION,
  );
  const [query, setQuery] = useState("");
  const [pendingNav, setPendingNav] = useState(false);
  const [whenSelection, setWhenSelection] = useState<WhenSelection>(EMPTY_WHEN);
  const [whoSelection, setWhoSelection] = useState<WhoSelection>(EMPTY_WHO);
  // Single expanded accordion section; Where leads so location comes first.
  const [expandedId, setExpandedId] = useState<string | null>("where");
  const [addPersonPresented, setAddPersonPresented] = useState(false);

  const { current_account, accounts, family_members } = usePage<SharedProps>().props;
  const isFamily = current_account?.profile_type === "Family";
  const familyAccount = useMemo(() => accounts?.find((account) => account.profile_type === "Family"), [accounts]);

  const { whereProps, whereBbox, whereFilter, resetWhere } = useSearchBarWhere({ types: activeSection.whereTypes });

  const go = useCallback(
    (path: string, data: Record<string, string>) => {
      router.get(path, data, VISIT_OPTIONS);
      onOpenChange(false);
    },
    [onOpenChange],
  );

  const resetSearch = useCallback(() => {
    setQuery("");
    setPendingNav(false);
    setWhenSelection(EMPTY_WHEN);
    setWhoSelection(EMPTY_WHO);
    setExpandedId("where");
    resetWhere();
  }, [resetWhere]);

  // Navigate to the active section's index with the current Where selection (plus the When window and
  // Who selection for sections that support them), once the selection describes an area — a bbox, or a
  // term filter that reconstructs one on the section page (mirrors DestinationsUrlSync).
  const submitWhere = useCallback(() => {
    if (whereProps.value && (whereBbox || whereFilter)) {
      go(activeSection.path, {
        ...serializeWhereParams({ selection: whereProps.value, bbox: whereBbox, filter: whereFilter }),
        ...(activeSection.hasWhen ? serializeWhenParams(whenSelection) : {}),
        ...(activeSection.hasWho ? serializeWhoParams(whoSelection) : {}),
      });
    }
  }, [go, activeSection, whereProps.value, whereBbox, whereFilter, whenSelection, whoSelection]);

  // Reset to the route's section and a clean input each time the overlay opens.
  useEffect(() => {
    if (!isOpen) return;
    setActiveSection(sectionForPath(currentPath) ?? DEFAULT_EXPLORE_SECTION);
    resetSearch();
  }, [isOpen, currentPath, resetSearch]);

  // Pending navigation resolves once the area does (synchronous for destinations and regions' term
  // filters, async for "nearby"). The `isOpen` guard avoids navigating if it resolves after the overlay
  // was dismissed; clearing `pendingNav` on a null value handles geolocation denial.
  useEffect(() => {
    if (!isOpen || !pendingNav) return;
    if (whereProps.value && (whereBbox || whereFilter)) {
      submitWhere();
      setPendingNav(false);
    } else if (!whereProps.value) {
      setPendingNav(false);
    }
  }, [isOpen, pendingNav, whereProps.value, whereBbox, whereFilter, submitWhere]);

  const handleWhereChange = (value: WhereSelection | null) => {
    whereProps.onChange(value);
    // Sections with a When/Who step let the viewer keep building the search, advancing the accordion
    // to the next step; simpler Where-only sections navigate as soon as the selection resolves.
    if (activeSection.hasWhen) {
      if (value) setExpandedId("start");
    } else if (activeSection.hasWho) {
      if (value) setExpandedId("who");
    } else {
      setPendingNav(value != null);
    }
  };

  const handleWhenChange = (next: WhenSelection) => {
    const startJustSet = !whenSelection.start && !!next.start;
    setWhenSelection(next);
    if (startJustSet) setExpandedId("end");
  };

  const switchSection = (section: ExploreSection) => {
    setActiveSection(section);
    resetSearch();
  };

  const handleSearch = () => {
    if (activeSection.searchKind === "where") {
      setPendingNav(true);
      return;
    }
    const trimmed = query.trim();
    go(activeSection.path, trimmed ? { [MARKETPLACE_QUERY_PARAM]: trimmed } : {});
  };

  const searchDisabled = activeSection.searchKind === "where" && !(whereProps.value && (whereBbox || whereFilter));

  // Only built for sections that render the stacked accordion (any of When/Who) — skips the panel
  // elements + i18n lookups entirely for marketplace and Where-only sections.
  const usesAccordion = activeSection.hasWhen || activeSection.hasWho;
  const accordionSections: SearchSectionStackItem[] = usesAccordion
    ? [
        {
          id: "where",
          heading: t("components.search_bar.where_title"),
          summary: whereProps.value?.name,
          children: <WhereSearchPanel {...whereProps} onChange={handleWhereChange} />,
        },
        ...(activeSection.hasWhen
          ? [
              {
                id: "start",
                heading: t("components.search_bar.when_start_title"),
                children: <WhenStartPanel onChange={handleWhenChange} value={whenSelection} />,
              },
              {
                id: "end",
                heading: t("components.search_bar.when_end_title"),
                children: <WhenEndPanel onChange={handleWhenChange} value={whenSelection} />,
              },
            ]
          : []),
        ...(activeSection.hasWho
          ? [
              {
                id: "who",
                heading: t("components.search_bar.who_heading"),
                summary: whoSummary(whoSelection, family_members) ?? undefined,
                children: (
                  <WhoPanel
                    hasFamilyAccount={!!familyAccount}
                    isFamily={isFamily}
                    members={family_members}
                    onAddPerson={isFamily ? () => setAddPersonPresented(true) : undefined}
                    onChange={setWhoSelection}
                    onCreateFamily={() => router.visit(newAccountPath())}
                    onSwitchToFamily={
                      familyAccount
                        ? () => router.post(accountSwitchPath(), { account_id: familyAccount.id })
                        : undefined
                    }
                    value={whoSelection}
                  />
                ),
              },
            ]
          : []),
      ]
    : [];

  return (
    // Fullscreen search experience with its own tab header — the Page surface
    // rather than the default Dialog.
    <Page.Root isOpen={isOpen} onOpenChange={onOpenChange}>
      <Page.Content>
        <DialogTitle className="sr-only">{t("components.frames.app_layout.explore_search.overlay_title")}</DialogTitle>

        {isActive ? (
          <>
            {/* The close button is a row item, not an overlay: at phone widths the
                four tabs outgrow the row, and anything floated on top of a strip
                that scrolls under it collides with whichever tab is passing. The
                header owns the rule so it still spans the full width. */}
            <header className="flex shrink-0 items-center border-neutral-100 border-b pt-5 pe-5">
              <SubNav
                aria-label={t("components.frames.app_layout.explore_search.tabs_aria_label")}
                // Plain justify-center puts the first tab in overflow that a scroll
                // container cannot reach — safe centring falls back to start.
                className="min-w-0 flex-1 justify-center-safe overflow-x-auto border-b-0 scrollbar-hide [&>*]:shrink-0"
              >
                {EXPLORE_SECTIONS.map((section) => (
                  <SubNavItem
                    key={section.key}
                    icon={section.icon}
                    label={t(section.labelKey)}
                    color={section.color}
                    isActive={section.key === activeSection.key}
                    onPress={() => switchSection(section)}
                  />
                ))}
              </SubNav>
              <Button
                aria-label={t("common.actions.close")}
                className={`mb-2 shrink-0 ${CLOSE_BUTTON_CLASS}`}
                onPress={() => onOpenChange(false)}
              >
                <Icon name="exit" size="20" />
              </Button>
            </header>

            <div className="flex-1 overflow-y-auto p-5">
              {activeSection.searchKind === "query" ? (
                <div className={`flex flex-col gap-3 p-5 ${SEARCH_CARD}`}>
                  <h2 className="text-2xl/8 font-extrabold tracking-2xl text-neutral-900">
                    {t("components.frames.app_layout.explore_search.marketplace_title")}
                  </h2>
                  <MarketplaceSearchBar onChange={setQuery} value={query} />
                </div>
              ) : usesAccordion ? (
                // Stacked Where → Start date → End date → Who accordion (Figma mobile search).
                <OverlayTriggerStateContext.Provider value={NOOP_OVERLAY_STATE}>
                  <SearchSectionStack
                    expandedId={expandedId}
                    onExpandedChange={setExpandedId}
                    sections={accordionSections}
                  />
                </OverlayTriggerStateContext.Provider>
              ) : (
                <div className={SEARCH_CARD}>
                  {/* WherePopover supplies its own p-5/gap-3 + "Where to?" heading + input + suggestions. */}
                  <OverlayTriggerStateContext.Provider value={NOOP_OVERLAY_STATE}>
                    <WherePopover {...whereProps} onChange={handleWhereChange} />
                  </OverlayTriggerStateContext.Provider>
                </div>
              )}
            </div>

            <Page.Footer>
              <Button
                className="cursor-pointer text-base/6 font-medium text-neutral-600 outline-none hover:text-neutral-900 focus-visible:rounded focus-visible:ring-2 focus-visible:ring-neutral-400"
                onPress={resetSearch}
              >
                {t("components.frames.app_layout.explore_search.clear_all")}
              </Button>
              <StandardButton
                color="pink"
                hierarchy="primary"
                isDisabled={searchDisabled}
                leadIcon={<Icon name="search" />}
                onPress={handleSearch}
                size="44"
              >
                {t("components.frames.app_layout.explore_search.search")}
              </StandardButton>
            </Page.Footer>
          </>
        ) : (
          <>
            <header className="flex shrink-0 items-center justify-end px-5 pt-5">
              <Button
                aria-label={t("common.actions.close")}
                className={CLOSE_BUTTON_CLASS}
                onPress={() => onOpenChange(false)}
              >
                <Icon name="exit" size="20" />
              </Button>
            </header>
            <div className="flex flex-1 items-center justify-center px-5 pb-10">
              <UnderConstructionView
                title={t("web.under_construction.show.title")}
                description={t("web.under_construction.show.description")}
              />
            </div>
          </>
        )}
        <AddPersonSheet
          onAdded={(personId) => setWhoSelection((current) => ({ memberIds: [...current.memberIds, personId] }))}
          onPresentedChange={setAddPersonPresented}
          presented={addPersonPresented}
        />
      </Page.Content>
    </Page.Root>
  );
}
